import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { TwinChat } from "@/components/TwinChat";
import {
  Bot,
  RefreshCw,
  Save,
  Code2,
  Copy,
  ExternalLink,
  Sparkles,
  Building2,
  Globe,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { motion } from "framer-motion";
import { LogoUploader } from "@/components/LogoUploader";
import { TrustSection } from "@/components/twin/TrustSection";
import { ServiceOfferingSection } from "@/components/twin/ServiceOfferingSection";
import {
  getMyCompany,
  updateCompany,
  regeneratePersonality,
  generateCompanyDescription,
} from "@/lib/company.functions";
import { logTurn, listConversations } from "@/lib/conversations.functions";
import { toast } from "sonner";
import { useHydratedTranslation } from "@/components/marketing/Layout";

import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/twin")({
  head: () =>
    buildSeo({
      path: "/twin",
      title: "AI Twin — Reality Twin",
      description:
        "Configure your AI Twin's persona, knowledge and conversation style — the digital counterpart that represents your company across the Twin network.",
      noindex: true,
    }),
  component: TwinPage,
});

// Everything about the twin now lives here: who it represents, how it speaks,
// what it may claim, and what it may sell. Settings keeps only account-level
// concerns (team, integrations, notifications, security).
type TwinTab = "chat" | "profile" | "personality" | "trust" | "services";

function TwinPage() {
  const t = useHydratedTranslation();
  const fetchCompany = useServerFn(getMyCompany);
  const saveCompany = useServerFn(updateCompany);
  const regenerate = useServerFn(regeneratePersonality);
  const genDescription = useServerFn(generateCompanyDescription);
  const logConv = useServerFn(logTurn);
  const listConvs = useServerFn(listConversations);

  const [company, setCompany] = useState<any>(null);
  const [tab, setTab] = useState<TwinTab>("chat");
  const [personality, setPersonality] = useState("");
  const [profile, setProfile] = useState({
    name: "",
    website: "",
    industry: "",
    country: "",
    size: "",
    description: "",
    is_public: false,
  });
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [genDesc, setGenDesc] = useState(false);
  const [regen, setRegen] = useState(false);
  const [convId, setConvId] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    fetchCompany().then((r) => {
      setCompany(r.company);
      setPersonality(r.company?.personality ?? "");
      if (r.company)
        setProfile({
          name: r.company.name ?? "",
          website: r.company.website ?? "",
          industry: r.company.industry ?? "",
          country: r.company.country ?? "",
          size: (r.company as any).size ?? "",
          description: r.company.description ?? "",
          is_public: !!r.company.is_public,
        });
    });
    listConvs()
      .then((r) => setHistory(r.conversations.slice(0, 8)))
      .catch(() => {});
  }, [fetchCompany, listConvs]);

  // System prompt and RAG are built server-side in /api/chat from the
  // trusted company row; the client only sends companyId. The shared TwinChat
  // component handles markdown, citations and follow-ups; each completed
  // exchange is logged to Conversations via logTurn.
  async function handleTurn(userText: string, assistantText: string) {
    try {
      const r = await logConv({
        data: { conversationId: convId, userMessage: userText, assistantMessage: assistantText },
      });
      setConvId(r.conversationId);
    } catch (e) {
      console.warn("logTurn failed", e);
    }
  }

  if (!company)
    return (
      <DashboardShell title="AI Twin">
        <div className="glass-strong rounded-2xl p-10 text-center">
          <p className="text-muted-foreground mb-4">{t("app.pages.twin.createProfilePrompt")}</p>
          <Link
            to="/onboarding"
            className="rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium"
          >
            {t("app.pages.twin.startOnboarding")}
          </Link>
        </div>
      </DashboardShell>
    );

  async function savePersonality() {
    setSaving(true);
    try {
      await saveCompany({ data: { personality } });
      const r = await fetchCompany();
      setCompany(r.company);
      toast.success(t("app.pages.twin.personalitySaved"));
    } catch (e: any) {
      toast.error(e.message ?? t("app.pages.twin.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function doRegenerate() {
    setRegen(true);
    try {
      const r = await regenerate({});
      setPersonality(r.personality);
      const c = await fetchCompany();
      setCompany(c.company);
      toast.success(t("app.pages.twin.personalityRegen"));
    } catch (e: any) {
      toast.error(e.message ?? t("app.pages.twin.regenFailed"));
    } finally {
      setRegen(false);
    }
  }

  async function saveProfile() {
    setSavingProfile(true);
    try {
      await saveCompany({
        data: {
          name: profile.name,
          website: profile.website,
          industry: profile.industry,
          country: profile.country,
          size: profile.size,
          description: profile.description,
          is_public: profile.is_public,
        },
      });
      const r = await fetchCompany();
      setCompany(r.company);
      toast.success(t("app.pages.twin.profileSaved"));
    } catch (e: any) {
      toast.error(e.message ?? t("app.pages.twin.saveFailed"));
    } finally {
      setSavingProfile(false);
    }
  }

  // The logo saves immediately rather than waiting for "Save profile" — the
  // upload already happened, so leaving it unsaved would be a silent loss.
  async function saveLogo(url: string) {
    await saveCompany({ data: { logo_url: url } });
    const r = await fetchCompany();
    setCompany(r.company);
  }

  async function doGenerateDescription() {
    setGenDesc(true);
    try {
      const r = await genDescription({});
      setProfile((p) => ({ ...p, description: r.description }));
      const c = await fetchCompany();
      setCompany(c.company);
      toast.success(t("app.pages.twin.descWritten"));
    } catch (e: any) {
      toast.error(e.message ?? t("app.pages.twin.couldNotGenerate"));
    } finally {
      setGenDesc(false);
    }
  }

  return (
    <DashboardShell
      title="AI Twin"
      subtitle={t("app.pages.twin.subtitle", { name: company.name })}
      actions={
        <div className="-mx-1 max-w-full overflow-x-auto px-1 pb-1">
          <div className="inline-flex items-center rounded-full glass border border-border p-1 text-sm">
            {(
              [
                ["chat", t("app.pages.twin.tabChat"), Bot],
                ["profile", t("app.pages.twin.tabProfile"), Building2],
                ["personality", t("app.pages.twin.tabPersonality"), Sparkles],
                ["trust", t("app.pages.twin.tabTrust", { defaultValue: "Trust" }), ShieldCheck],
                ["services", t("app.pages.twin.tabServices", { defaultValue: "Services" }), Wrench],
              ] as const
            ).map(([id, label, Icon]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full transition whitespace-nowrap ${
                  tab === id
                    ? "brand-gradient text-black font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
      }
    >
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-4">
          <div className="glass-strong rounded-2xl p-6 text-center">
            <div className="relative mx-auto h-28 w-28">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute inset-0 rounded-full brand-gradient glow-mint"
              />
              <div className="absolute inset-2 overflow-hidden rounded-full bg-background flex items-center justify-center">
                {company.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={`${company.name} logo`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="font-display text-3xl font-bold text-gradient">
                    {company.name?.[0] ?? "?"}
                  </span>
                )}
              </div>
            </div>
            <div className="mt-4 font-display text-xl font-semibold">{company.name}</div>
            <div className="text-xs text-[oklch(0.82_0.18_168)] flex items-center justify-center gap-1.5 mt-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[oklch(0.82_0.18_168)] animate-pulse" />{" "}
              {t("app.pages.twin.twinStatus", { status: company.twin_status })}
            </div>
            <p className="mt-3 text-xs text-muted-foreground line-clamp-6">
              {company.personality || t("app.pages.twin.noPersonality")}
            </p>
          </div>

          <div className="glass-strong rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-3">
              {t("app.pages.twin.knowledgeBase")}
            </div>
            <div className="space-y-2.5 text-sm">
              {[
                [t("app.pages.twin.kbDocuments"), company.documents_indexed],
                [t("app.pages.twin.kbSize"), company.knowledge_size],
                [t("app.pages.twin.kbAiScore"), company.ai_score],
              ].map(([k, v]) => (
                <div key={String(k)} className="flex justify-between">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-medium">{String(v)}</span>
                </div>
              ))}
            </div>
            <Link
              to="/knowledge"
              className="mt-4 w-full rounded-xl glass border border-border py-2 text-sm hover:bg-white/5 flex items-center justify-center gap-1.5"
            >
              {t("app.pages.twin.updateKnowledge")}
            </Link>
          </div>

          <div className="glass-strong rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-3">
              {t("app.pages.twin.recentConversations")}
            </div>
            {history.length === 0 ? (
              <div className="text-xs text-muted-foreground">
                {t("app.pages.twin.noConversations")}
              </div>
            ) : (
              <ul className="space-y-2">
                {history.map((h) => (
                  <li key={h.id}>
                    <Link
                      to="/conversations"
                      className="block text-sm truncate hover:text-[oklch(0.82_0.18_168)]"
                    >
                      {h.title ?? t("app.pages.twin.conversationFallback")}
                    </Link>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(h.created_at).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <Link
              to="/conversations"
              className="mt-3 block text-xs text-[oklch(0.82_0.18_168)] hover:underline"
            >
              {t("app.pages.twin.viewAll")}
            </Link>
          </div>

          <InstallSnippet
            companyId={company.id}
            isPublic={!!company.is_public}
            onOpenProfile={() => setTab("profile")}
          />
        </div>

        {tab === "chat" && (
          <div className="lg:col-span-2 glass-strong rounded-2xl flex flex-col h-[calc(100vh-200px)] min-h-[500px]">
            <div className="px-5 py-4 border-b border-border flex items-center gap-3">
              <Bot className="h-5 w-5 text-[oklch(0.82_0.18_168)]" />
              <div>
                <div className="font-semibold text-sm">{t("app.pages.twin.interactiveChat")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("app.pages.twin.interactiveChatSub")}
                </div>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <TwinChat
                companyId={company.id}
                companyName={company.name}
                mode="owner"
                onTurn={handleTurn}
                starterQuestions={[
                  t("app.pages.twin.starter1"),
                  t("app.pages.twin.starter2"),
                  t("app.pages.twin.starter3"),
                ]}
                placeholder={t("app.pages.twin.chatPlaceholder")}
              />
            </div>
          </div>
        )}

        {tab === "profile" && (
          <div className="lg:col-span-2 glass-strong rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                {t("app.pages.twin.profileTitle")}
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">{t("app.pages.twin.profileSub")}</p>

            <div className="rounded-xl border border-border bg-white/[0.02] p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-3">
                {t("app.pages.settings.companyLogo")}
              </div>
              <LogoUploader
                logoUrl={company.logo_url}
                companyName={profile.name || company.name}
                onChange={saveLogo}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={t("app.pages.settings.fName")}>
                <input
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className={inp}
                />
              </Field>
              <Field label={t("app.pages.settings.fWebsite")}>
                <input
                  value={profile.website}
                  onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                  placeholder="https://…"
                  className={inp}
                />
              </Field>
              <Field label={t("app.pages.settings.fIndustry")}>
                <input
                  value={profile.industry}
                  onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
                  className={inp}
                />
              </Field>
              <Field label={t("app.pages.settings.fCountry")}>
                <input
                  value={profile.country}
                  onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                  className={inp}
                />
              </Field>
              <Field label={t("app.pages.settings.fSize")}>
                <input
                  value={profile.size}
                  onChange={(e) => setProfile({ ...profile, size: e.target.value })}
                  placeholder="1-10"
                  className={inp}
                />
              </Field>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                  {t("app.pages.twin.descLabel")}
                </div>
                <button
                  onClick={doGenerateDescription}
                  disabled={genDesc}
                  className="inline-flex items-center gap-1.5 rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5 disabled:opacity-50"
                >
                  <Sparkles className={`h-3.5 w-3.5 ${genDesc ? "animate-pulse" : ""}`} />
                  {genDesc ? t("app.pages.twin.writing") : t("app.pages.twin.generateAI")}
                </button>
              </div>
              <textarea
                value={profile.description}
                onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                rows={8}
                className={`${inp} resize-none`}
                placeholder={t("app.pages.twin.descPlaceholder")}
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-border bg-white/[0.02] p-4">
              <input
                type="checkbox"
                checked={profile.is_public}
                onChange={(e) => setProfile({ ...profile, is_public: e.target.checked })}
                className="h-4 w-4 mt-0.5 accent-[oklch(0.82_0.18_168)] shrink-0"
              />
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5 text-[oklch(0.82_0.18_168)]" />
                  {t("app.pages.settings.publishTwin")}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t("app.pages.settings.publishTwinSub")}
                </div>
              </div>
            </label>

            <button
              onClick={saveProfile}
              disabled={savingProfile}
              className="rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" />{" "}
              {savingProfile ? t("app.pages.common.saving") : t("app.pages.twin.saveProfileBtn")}
            </button>
          </div>
        )}

        {tab === "trust" && (
          <div className="lg:col-span-2">
            <TrustSection />
          </div>
        )}

        {tab === "services" && (
          <div className="lg:col-span-2">
            <ServiceOfferingSection />
          </div>
        )}

        {tab === "personality" && (
          <div className="lg:col-span-2 glass-strong rounded-2xl p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                {t("app.pages.twin.personalityTitle")}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t("app.pages.twin.personalitySub")}
              </p>
            </div>
            <textarea
              value={personality}
              onChange={(e) => setPersonality(e.target.value)}
              rows={16}
              className="w-full rounded-xl bg-white/5 border border-border p-4 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              placeholder={t("app.pages.twin.personalityPlaceholder")}
            />
            <div className="flex gap-2">
              <button
                onClick={savePersonality}
                disabled={saving}
                className="rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" />{" "}
                {saving ? t("app.pages.common.saving") : t("app.pages.twin.savePersonalityBtn")}
              </button>
              <button
                onClick={doRegenerate}
                disabled={regen}
                className="rounded-full glass border border-border px-5 py-2 text-sm hover:bg-white/5 flex items-center gap-1.5 disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${regen ? "animate-spin" : ""}`} />{" "}
                {regen ? t("app.pages.twin.generating") : t("app.pages.twin.regenerateBtn")}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

const inp =
  "w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}

function InstallSnippet({
  companyId,
  isPublic,
  onOpenProfile,
}: {
  companyId: string;
  isPublic: boolean;
  onOpenProfile: () => void;
}) {
  const t = useHydratedTranslation();
  const origin = typeof window !== "undefined" ? window.location.origin : "https://realitytwin.io";
  const snippet = `<script src="${origin}/api/public/embed/${companyId}" async></script>`;
  const embedUrl = `${origin}/embed/${companyId}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success(t("app.pages.twin.snippetCopied"));
    } catch {
      toast.error(t("app.pages.twin.couldNotCopy"));
    }
  }

  return (
    <div className="glass-strong rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <Code2 className="h-4 w-4 text-[oklch(0.68_0.19_290)]" />
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
          {t("app.pages.twin.installTitle")}
        </div>
      </div>
      <div className="font-display text-sm font-semibold mb-2">
        {t("app.pages.twin.embedTitle")}
      </div>
      {!isPublic ? (
        <p className="text-xs text-muted-foreground">
          {t("app.pages.twin.makePublicBefore")}
          <button onClick={onOpenProfile} className="text-[oklch(0.82_0.18_168)] hover:underline">
            {t("app.pages.twin.tabProfile")}
          </button>
          {t("app.pages.twin.makePublicAfter")}
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-2">
            {t("app.pages.twin.pasteBefore")}
            <code className="px-1 rounded bg-white/5">&lt;/body&gt;</code>
            {t("app.pages.twin.pasteAfter")}
          </p>
          <pre className="text-[11px] bg-black/40 border border-border rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all font-mono">
            {snippet}
          </pre>
          <div className="mt-2 flex gap-2">
            <button
              onClick={copy}
              className="flex-1 rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5 flex items-center justify-center gap-1.5"
            >
              <Copy className="h-3 w-3" /> {t("app.pages.twin.copySnippet")}
            </button>
            <a
              href={embedUrl}
              target="_blank"
              rel="noopener"
              className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5 flex items-center gap-1.5"
            >
              <ExternalLink className="h-3 w-3" /> {t("app.pages.twin.preview")}
            </a>
          </div>
        </>
      )}
    </div>
  );
}
